import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { Formik } from 'formik'
import * as Yup from 'yup'

import { FamilyStatus, SetIchraProductClassAllowanceRequest } from 'core'

import { CommandResponse } from 'common/hooks/useCommandRequestV2'

import { Alert, Col, DefaultInput, DefaultSelect, Icon, Radio, Row } from 'common/components'

import { BaseComponentProps } from '../../Common'

import './styles.less'

enum AllowanceTypes {
  SameAllowance = 0,
  DifferentAllowance = 1,
  AgeBasedAllowance = 2,
  FamilyAgeBasedAllowance = 3,
}

const allowancesOptions: {
  id: number
  title: string
}[] = [
  {
    id: AllowanceTypes.SameAllowance,
    title: 'Give all employees the same allowance',
  },
  {
    id: AllowanceTypes.DifferentAllowance,
    title: 'Give a higher allowance to employees who are married and/or have other dependents.',
  },
  {
    id: AllowanceTypes.AgeBasedAllowance,
    title: 'Give a higher allowance to older employees based on their age only',
  },
  {
    id: AllowanceTypes.FamilyAgeBasedAllowance,
    title: 'Give a higher allowance to employees based on their age and family status',
  },
]

const oldestAgeOptions = [...new Array(58)].map((each, index) => {
  const value = 75 - index
  return { label: value, value }
})

const youngestAgeOptions = [...new Array(58)].map((each, index) => {
  const value = index + 18
  return { label: value, value }
})

export type SetAllowancesProps = {
  productClassId: string
  familyAllowances: any[]
  offerSameAllowance: boolean
  planPeriodId?: string
}

const SetAllowances = (
  props: BaseComponentProps<SetAllowancesProps> & { commandHandler?: (values: any) => Promise<CommandResponse<void>> },
) => {
  const { t: translate } = useTranslation()
  const { bindSubmitFormHandler } = props
  const { planPeriod } = useParams<any>()

  const [initialValues] = useState({
    allowanceOptionId:
      props.state.offerSameAllowance !== null
        ? props.state.offerSameAllowance
          ? props.state.familyAllowances[0].age === 0
            ? AllowanceTypes.SameAllowance
            : AllowanceTypes.AgeBasedAllowance
          : props.state.familyAllowances[0].age === 0
          ? AllowanceTypes.DifferentAllowance
          : AllowanceTypes.FamilyAgeBasedAllowance
        : null,
    sameAllowanceAmount:
      props.state.offerSameAllowance && props.state.familyAllowances.find((x) => x.age === 0)
        ? props.state.familyAllowances.find((x) => x.familyStatusId === FamilyStatus.Single)?.amount
        : null,
    singleAmount: props.state.familyAllowances.find((x) => x.age === 0)
      ? props.state.familyAllowances.find((x) => x.familyStatusId === FamilyStatus.Single)?.amount
      : null,
    singleWithDependentsAmount: props.state.familyAllowances.find((x) => x.age === 0)
      ? props.state.familyAllowances.find((x) => x.familyStatusId === FamilyStatus.SingleWithDependents)?.amount
      : null,
    marriedAmount: props.state.familyAllowances.find((x) => x.age === 0)
      ? props.state.familyAllowances.find((x) => x.familyStatusId === FamilyStatus.Married)?.amount
      : null,
    marriedWithDependentsAmount: props.state.familyAllowances.find((x) => x.age === 0)
      ? props.state.familyAllowances.find((x) => x.familyStatusId === FamilyStatus.MarriedWithDependents)?.amount
      : null,
    oldestAge:
      props.state.offerSameAllowance && props.state.familyAllowances.find((x) => x.age !== 0)
        ? props.state.familyAllowances.sort((a, b) => (a.age < b.age ? 1 : -1))[0].age
        : null,
    oldestAmount:
      props.state.offerSameAllowance && props.state.familyAllowances.find((x) => x.age !== 0)
        ? props.state.familyAllowances.sort((a, b) => (a.age < b.age ? 1 : -1))[0].amount
        : null,
    youngestAge:
      props.state.offerSameAllowance && props.state.familyAllowances.find((x) => x.age !== 0)
        ? props.state.familyAllowances.sort((a, b) => (a.age > b.age ? 1 : -1))[0].age
        : null,
    youngestAmount:
      props.state.offerSameAllowance && props.state.familyAllowances.find((x) => x.age !== 0)
        ? props.state.familyAllowances.sort((a, b) => (a.age > b.age ? 1 : -1))[0].amount
        : null,
    oldestFamilyAge:
      !props.state.offerSameAllowance && props.state.familyAllowances.find((x) => x.age !== 0)
        ? props.state.familyAllowances.sort((a, b) => (a.age < b.age ? 1 : -1))[0].age
        : null,
    oldestSingleAmount:
      !props.state.offerSameAllowance && props.state.familyAllowances.find((x) => x.age !== 0)
        ? props.state.familyAllowances
            .sort((a, b) => (a.age < b.age ? 1 : -1))
            .find((x) => x.familyStatusId === FamilyStatus.Single).amount
        : null,
    oldestSingleWithDependentsAmount:
      !props.state.offerSameAllowance && props.state.familyAllowances.find((x) => x.age !== 0)
        ? props.state.familyAllowances
            .sort((a, b) => (a.age < b.age ? 1 : -1))
            .find((x) => x.familyStatusId === FamilyStatus.SingleWithDependents).amount
        : null,
    oldestMarriedAmount:
      !props.state.offerSameAllowance && props.state.familyAllowances.find((x) => x.age !== 0)
        ? props.state.familyAllowances
            .sort((a, b) => (a.age < b.age ? 1 : -1))
            .find((x) => x.familyStatusId === FamilyStatus.Married).amount
        : null,
    oldestMarriedWithDependentsAmount:
      !props.state.offerSameAllowance && props.state.familyAllowances.find((x) => x.age !== 0)
        ? props.state.familyAllowances
            .sort((a, b) => (a.age < b.age ? 1 : -1))
            .find((x) => x.familyStatusId === FamilyStatus.MarriedWithDependents).amount
        : null,
    youngestFamilyAge:
      !props.state.offerSameAllowance && props.state.familyAllowances.find((x) => x.age !== 0)
        ? props.state.familyAllowances.sort((a, b) => (a.age > b.age ? 1 : -1))[0].age
        : null,
    youngestSingleAmount:
      !props.state.offerSameAllowance && props.state.familyAllowances.find((x) => x.age !== 0)
        ? props.state.familyAllowances
            .sort((a, b) => (a.age > b.age ? 1 : -1))
            .find((x) => x.familyStatusId === FamilyStatus.Single).amount
        : null,
    youngestSingleWithDependentsAmount:
      !props.state.offerSameAllowance && props.state.familyAllowances.find((x) => x.age !== 0)
        ? props.state.familyAllowances
            .sort((a, b) => (a.age > b.age ? 1 : -1))
            .find((x) => x.familyStatusId === FamilyStatus.SingleWithDependents).amount
        : null,
    youngestMarriedAmount:
      !props.state.offerSameAllowance && props.state.familyAllowances.find((x) => x.age !== 0)
        ? props.state.familyAllowances
            .sort((a, b) => (a.age > b.age ? 1 : -1))
            .find((x) => x.familyStatusId === FamilyStatus.Married).amount
        : null,
    youngestMarriedWithDependentsAmount:
      !props.state.offerSameAllowance && props.state.familyAllowances.find((x) => x.age !== 0)
        ? props.state.familyAllowances
            .sort((a, b) => (a.age > b.age ? 1 : -1))
            .find((x) => x.familyStatusId === FamilyStatus.MarriedWithDependents).amount
        : null,
  })

  useEffect(() => {
    props.setIsFormValid(formValidator(translate).isValidSync(initialValues))
  }, [initialValues])

  const onSubmit = async (values) => {
    const offerSameAllowance =
      values.allowanceOptionId === AllowanceTypes.SameAllowance ||
      values.allowanceOptionId === AllowanceTypes.AgeBasedAllowance

    const request: SetIchraProductClassAllowanceRequest = {
      planPeriodId: planPeriod ?? props.planPeriodId,
      productClassId: props.state.productClassId,
      offerSameAllowance: offerSameAllowance,
      allowanceAmounts:
        values.allowanceOptionId === AllowanceTypes.SameAllowance ||
        values.allowanceOptionId === AllowanceTypes.DifferentAllowance
          ? [
              {
                familyStatusId: FamilyStatus.Single,
                amount: offerSameAllowance ? Number(values.sameAllowanceAmount) : Number(values.singleAmount),
                age: 0,
              },
              {
                familyStatusId: FamilyStatus.SingleWithDependents,
                amount: offerSameAllowance
                  ? Number(values.sameAllowanceAmount)
                  : Number(values.singleWithDependentsAmount),
                age: 0,
              },
              {
                familyStatusId: FamilyStatus.Married,
                amount: offerSameAllowance ? Number(values.sameAllowanceAmount) : Number(values.marriedAmount),
                age: 0,
              },
              {
                familyStatusId: FamilyStatus.MarriedWithDependents,
                amount: offerSameAllowance
                  ? Number(values.sameAllowanceAmount)
                  : Number(values.marriedWithDependentsAmount),
                age: 0,
              },
            ]
          : [
              {
                familyStatusId: FamilyStatus.Single,
                amount:
                  values.allowanceOptionId === AllowanceTypes.AgeBasedAllowance
                    ? Number(values.youngestAmount)
                    : Number(values.youngestSingleAmount),
                age:
                  values.allowanceOptionId === AllowanceTypes.AgeBasedAllowance
                    ? values.youngestAge
                    : values.youngestFamilyAge,
              },
              {
                familyStatusId: FamilyStatus.SingleWithDependents,
                amount:
                  values.allowanceOptionId === AllowanceTypes.AgeBasedAllowance
                    ? Number(values.youngestAmount)
                    : Number(values.youngestSingleWithDependentsAmount),
                age:
                  values.allowanceOptionId === AllowanceTypes.AgeBasedAllowance
                    ? values.youngestAge
                    : values.youngestFamilyAge,
              },
              {
                familyStatusId: FamilyStatus.Married,
                amount:
                  values.allowanceOptionId === AllowanceTypes.AgeBasedAllowance
                    ? Number(values.youngestAmount)
                    : Number(values.youngestMarriedAmount),
                age:
                  values.allowanceOptionId === AllowanceTypes.AgeBasedAllowance
                    ? values.youngestAge
                    : values.youngestFamilyAge,
              },
              {
                familyStatusId: FamilyStatus.MarriedWithDependents,
                amount:
                  values.allowanceOptionId === AllowanceTypes.AgeBasedAllowance
                    ? Number(values.youngestAmount)
                    : Number(values.youngestMarriedWithDependentsAmount),
                age:
                  values.allowanceOptionId === AllowanceTypes.AgeBasedAllowance
                    ? values.youngestAge
                    : values.youngestFamilyAge,
              },
              {
                familyStatusId: FamilyStatus.Single,
                amount:
                  values.allowanceOptionId === AllowanceTypes.AgeBasedAllowance
                    ? Number(values.oldestAmount)
                    : Number(values.oldestSingleAmount),
                age:
                  values.allowanceOptionId === AllowanceTypes.AgeBasedAllowance
                    ? values.oldestAge
                    : values.oldestFamilyAge,
              },
              {
                familyStatusId: FamilyStatus.SingleWithDependents,
                amount:
                  values.allowanceOptionId === AllowanceTypes.AgeBasedAllowance
                    ? Number(values.oldestAmount)
                    : Number(values.oldestSingleWithDependentsAmount),
                age:
                  values.allowanceOptionId === AllowanceTypes.AgeBasedAllowance
                    ? values.oldestAge
                    : values.oldestFamilyAge,
              },
              {
                familyStatusId: FamilyStatus.Married,
                amount:
                  values.allowanceOptionId === AllowanceTypes.AgeBasedAllowance
                    ? Number(values.oldestAmount)
                    : Number(values.oldestMarriedAmount),
                age:
                  values.allowanceOptionId === AllowanceTypes.AgeBasedAllowance
                    ? values.oldestAge
                    : values.oldestFamilyAge,
              },
              {
                familyStatusId: FamilyStatus.MarriedWithDependents,
                amount:
                  values.allowanceOptionId === AllowanceTypes.AgeBasedAllowance
                    ? Number(values.oldestAmount)
                    : Number(values.oldestMarriedWithDependentsAmount),
                age:
                  values.allowanceOptionId === AllowanceTypes.AgeBasedAllowance
                    ? values.oldestAge
                    : values.oldestFamilyAge,
              },
            ],
    }

    const result = await props.commandHandler(request)

    if (result.completed) {
      props.goNext()
    } else {
      props.setIsSubmiting(false)
    }
  }

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={formValidator(translate)}
      initialValues={initialValues}
      onSubmit={onSubmit}
      validateOnChange={true}
      validate={(values) => {
        const isValid: boolean = formValidator(translate).isValidSync(values)
        props.setHasDirtyData(isValid)
        props.setIsFormValid(isValid)
        return {}
      }}
    >
      {(formik) => {
        const { handleSubmit, submitForm } = formik
        bindSubmitFormHandler(submitForm)

        return (
          <form autoComplete="off" onSubmit={handleSubmit} className={'ichra-set-allwances-wrapper'}>
            <h5 className="select_class_info mb-24">Set allowance amounts:</h5>

            <Radio.Group
              name="allowanceOptionId"
              className="ichra-set-allowances ichra-benefit-design-radio-group"
              onChange={(e) => {
                formik.setErrors({})
              }}
            >
              {allowancesOptions.map((item, index) => (
                <Radio.Button key={`${item.id}-${index}`} value={item.id} size="small" className="mb-8">
                  <p className="mb-0">{item.title}</p>
                </Radio.Button>
              ))}
            </Radio.Group>

            {formik.values.allowanceOptionId == AllowanceTypes.SameAllowance && <SameFamilyAllowanceInput />}
            {formik.values.allowanceOptionId == AllowanceTypes.DifferentAllowance && (
              <DifferentFamilyAllowancesInputs />
            )}
            {formik.values.allowanceOptionId == AllowanceTypes.AgeBasedAllowance && <AgeBasedAllowanceInputs />}
            {formik.values.allowanceOptionId == AllowanceTypes.FamilyAgeBasedAllowance && (
              <FamiltyAgeBasedAllowanceInputs />
            )}
          </form>
        )
      }}
    </Formik>
  )
}

const SameFamilyAllowanceInput = () => {
  return (
    <div className={'ichra-allowances-inputs'}>
      <DefaultInput
        label={'Monthly allowance amount'}
        name="sameAllowanceAmount"
        prefix={<Icon type="far fa-dollar-sign" fontAwesome />}
        placeholder={'0.00'}
      />
    </div>
  )
}

const DifferentFamilyAllowancesInputs = () => {
  return (
    <div className={'ichra-allowances-inputs ichra-different-allowances-inputs'}>
      <Row gutter={24}>
        <Col span={12} className={'mb-16'}>
          <DefaultInput
            label={'Single allowance'}
            name="singleAmount"
            prefix={<Icon type="far fa-dollar-sign" fontAwesome />}
            placeholder={'0.00'}
          />
        </Col>

        <Col span={12} className={'mb-16'}>
          <DefaultInput
            label={'Single with dependents allowance'}
            name="singleWithDependentsAmount"
            prefix={<Icon type="far fa-dollar-sign" fontAwesome />}
            placeholder={'0.00'}
          />
        </Col>

        <Col span={12}>
          <DefaultInput
            label={'Married allowance'}
            name="marriedAmount"
            prefix={<Icon type="far fa-dollar-sign" fontAwesome />}
            placeholder={'0.00'}
          />
        </Col>

        <Col span={12}>
          <DefaultInput
            label={'Married with dependents allowance'}
            name="marriedWithDependentsAmount"
            prefix={<Icon type="far fa-dollar-sign" fontAwesome />}
            placeholder={'0.00'}
          />
        </Col>
      </Row>
    </div>
  )
}

const AgeBasedAllowanceInputs = () => {
  return (
    <div className={'ichra-allowances-inputs'}>
      <Alert
        type="info"
        showIcon
        description={
          'Insurance premium can vary with age. Federal regulations allow you to provide up to three times the allowance for your oldest vs. youngest employees based on their age at the time the ICHRA benefits start.'
        }
      />
      <Row gutter={24}>
        <Col span={24} className={'mb-16 mt-16'}>
          <label className={'age-label'}>Oldest employees</label>
        </Col>
      </Row>
      <Row gutter={24}>
        <Col span={12} className={'mb-16'}>
          <DefaultSelect
            name="oldestAge"
            label={'Age'}
            items={oldestAgeOptions.map((p) => {
              return { id: p.value, name: p.label.toString() }
            })}
          ></DefaultSelect>
        </Col>
        <Col span={12} className={'mb-16'}>
          <DefaultInput
            label={'Allowance'}
            name="oldestAmount"
            prefix={<Icon type="far fa-dollar-sign" fontAwesome />}
            placeholder={'0.00'}
          />
        </Col>
      </Row>
      <hr></hr>
      <Row gutter={24}>
        <Col span={24} className={'mb-16 mt-16'}>
          <label className={'age-label'}>Youngest employees</label>
        </Col>
      </Row>
      <Row gutter={24}>
        <Col span={12} className={'mb-16'}>
          <DefaultSelect
            name="youngestAge"
            label={'Age'}
            items={youngestAgeOptions.map((p) => {
              return { id: p.value, name: p.label.toString() }
            })}
          ></DefaultSelect>
        </Col>
        <Col span={12} className={'mb-16'}>
          <DefaultInput
            label={'Allowance'}
            name="youngestAmount"
            prefix={<Icon type="far fa-dollar-sign" fontAwesome />}
            placeholder={'0.00'}
          />
        </Col>
      </Row>
    </div>
  )
}

const FamiltyAgeBasedAllowanceInputs = () => {
  return (
    <div className={'ichra-allowances-inputs'}>
      <Alert
        type="info"
        showIcon
        description={
          'Insurance premium can vary with age. Federal regulations allow you to provide up to three times the allowance for your oldest vs. youngest employees based on their age at the time the ICHRA benefits start.'
        }
      />
      <Row gutter={24}>
        <Col span={24} className={'mb-16 mt-16'}>
          <label className={'age-label'}>Oldest employees</label>
        </Col>
      </Row>
      <Row gutter={24}>
        <Col span={12} className={'mb-16'}>
          <DefaultSelect
            name="oldestFamilyAge"
            label={'Age'}
            items={oldestAgeOptions.map((p) => {
              return { id: p.value, name: p.label.toString() }
            })}
          ></DefaultSelect>
        </Col>
        <Col span={12} className={'mb-16'}></Col>
        <Col span={12} className={'mb-16'}>
          <DefaultInput
            label={'Single allowance'}
            name="oldestSingleAmount"
            prefix={<Icon type="far fa-dollar-sign" fontAwesome />}
            placeholder={'0.00'}
          />
        </Col>
        <Col span={12} className={'mb-16'}>
          <DefaultInput
            label={'Single with dependents allowance'}
            name="oldestSingleWithDependentsAmount"
            prefix={<Icon type="far fa-dollar-sign" fontAwesome />}
            placeholder={'0.00'}
          />
        </Col>
        <Col span={12}>
          <DefaultInput
            label={'Married allowance'}
            name="oldestMarriedAmount"
            prefix={<Icon type="far fa-dollar-sign" fontAwesome />}
            placeholder={'0.00'}
          />
        </Col>
        <Col span={12}>
          <DefaultInput
            label={'Married with dependents allowance'}
            name="oldestMarriedWithDependentsAmount"
            prefix={<Icon type="far fa-dollar-sign" fontAwesome />}
            placeholder={'0.00'}
          />
        </Col>
      </Row>
      <hr></hr>
      <Row gutter={24}>
        <Col span={24} className={'mb-16 mt-16'}>
          <label className={'age-label'}>Youngest employees</label>
        </Col>
      </Row>
      <Row gutter={24}>
        <Col span={12} className={'mb-16'}>
          <DefaultSelect
            name="youngestFamilyAge"
            label={'Age'}
            items={youngestAgeOptions.map((p) => {
              return { id: p.value, name: p.label.toString() }
            })}
          ></DefaultSelect>
        </Col>
        <Col span={12} className={'mb-16'}></Col>
        <Col span={12} className={'mb-16'}>
          <DefaultInput
            label={'Single allowance'}
            name="youngestSingleAmount"
            prefix={<Icon type="far fa-dollar-sign" fontAwesome />}
            placeholder={'0.00'}
          />
        </Col>
        <Col span={12} className={'mb-16'}>
          <DefaultInput
            label={'Single with dependents allowance'}
            name="youngestSingleWithDependentsAmount"
            prefix={<Icon type="far fa-dollar-sign" fontAwesome />}
            placeholder={'0.00'}
          />
        </Col>
        <Col span={12}>
          <DefaultInput
            label={'Married allowance'}
            name="youngestMarriedAmount"
            prefix={<Icon type="far fa-dollar-sign" fontAwesome />}
            placeholder={'0.00'}
          />
        </Col>
        <Col span={12}>
          <DefaultInput
            label={'Married with dependents allowance'}
            name="youngestMarriedWithDependentsAmount"
            prefix={<Icon type="far fa-dollar-sign" fontAwesome />}
            placeholder={'0.00'}
          />
        </Col>
      </Row>
    </div>
  )
}

const formValidator = (translate: any) =>
  Yup.object({
    allowanceOptionId: Yup.number().nullable().required(translate('general.required')),

    sameAllowanceAmount: Yup.number()
      .nullable()
      .typeError('Must be a number.')
      .when('allowanceOptionId', {
        is: (allowanceOptionId: AllowanceTypes) => allowanceOptionId === AllowanceTypes.SameAllowance,
        then: Yup.number()
          .nullable()
          .typeError('Must be a number.')
          .min(1, translate('admin-dashboard.benefit-details.class-design.errors.positive-number'))
          .required(translate('general.required')),
      }),

    singleAmount: Yup.number()
      .nullable()
      .when('allowanceOptionId', {
        is: (allowanceOptionId: AllowanceTypes) => allowanceOptionId === AllowanceTypes.DifferentAllowance,
        then: Yup.number()
          .nullable()
          .typeError('Must be a number.')
          .min(1, 'Must be greater than 0.')
          .required(translate('general.required')),
      }),

    singleWithDependentsAmount: Yup.number()
      .nullable()
      .when(['allowanceOptionId'], {
        is: (allowanceOptionId: AllowanceTypes) => allowanceOptionId === AllowanceTypes.DifferentAllowance,
        then: Yup.number()
          .nullable()
          .typeError('Must be a number.')
          .min(Yup.ref('singleAmount'), 'Must be greater than single allowance.')
          .required(translate('general.required')),
      }),

    marriedAmount: Yup.number()
      .nullable()
      .when('allowanceOptionId', {
        is: (allowanceOptionId: AllowanceTypes) => allowanceOptionId === AllowanceTypes.DifferentAllowance,
        then: Yup.number()
          .nullable()
          .typeError('Must be a number.')
          .min(Yup.ref('singleAmount'), 'Must be greater than single allowance.')
          .required(translate('general.required')),
      }),

    marriedWithDependentsAmount: Yup.number()
      .nullable()
      .when('allowanceOptionId', {
        is: (allowanceOptionId: AllowanceTypes) => allowanceOptionId === AllowanceTypes.DifferentAllowance,
        then: Yup.number()
          .nullable()
          .typeError('Must be a number.')
          .min(Yup.ref('singleAmount'), 'Must be greater than single allowance.')
          .required(translate('general.required')),
      }),

    oldestAge: Yup.number()
      .nullable()
      .when('allowanceOptionId', {
        is: (allowanceOptionId: AllowanceTypes) => allowanceOptionId === AllowanceTypes.AgeBasedAllowance,
        then: Yup.number()
          .nullable()
          .min(Yup.ref('youngestAge'), 'Must be greater than youngest age.')
          .required(translate('general.required')),
      }),

    oldestAmount: Yup.number()
      .nullable()
      .when('allowanceOptionId', {
        is: (allowanceOptionId: AllowanceTypes) => allowanceOptionId === AllowanceTypes.AgeBasedAllowance,
        then: Yup.number()
          .nullable()
          .typeError('Must be a number.')
          .min(Yup.ref('youngestAmount'), 'Must be greater than youngest allowance.')
          .required(translate('general.required')),
      })
      .test('is-greater-than-youngest-amount', 'Cannot be more than 3x the youngest allowance', function (value) {
        const { youngestAmount } = this.parent
        return youngestAmount === null || value <= youngestAmount * 3
      }),

    youngestAge: Yup.number()
      .nullable()
      .when('allowanceOptionId', {
        is: (allowanceOptionId: AllowanceTypes) => allowanceOptionId === AllowanceTypes.AgeBasedAllowance,
        then: Yup.number().nullable().required(translate('general.required')),
      }),

    youngestAmount: Yup.number()
      .nullable()
      .when('allowanceOptionId', {
        is: (allowanceOptionId: AllowanceTypes) => allowanceOptionId === AllowanceTypes.AgeBasedAllowance,
        then: Yup.number()
          .nullable()
          .typeError('Must be a number.')
          .min(1, 'Must be greater than 0.')
          .required(translate('general.required')),
      }),

    oldestFamilyAge: Yup.number()
      .nullable()
      .when('allowanceOptionId', {
        is: (allowanceOptionId: AllowanceTypes) => allowanceOptionId === AllowanceTypes.FamilyAgeBasedAllowance,
        then: Yup.number()
          .nullable()
          .min(Yup.ref('youngestFamilyAge'), 'Must be greater than youngest age.')
          .required(translate('general.required')),
      }),

    oldestSingleAmount: Yup.number()
      .nullable()
      .when('allowanceOptionId', {
        is: (allowanceOptionId: AllowanceTypes) => allowanceOptionId === AllowanceTypes.FamilyAgeBasedAllowance,
        then: Yup.number()
          .nullable()
          .typeError('Must be a number.')
          .min(1, 'Must be greater than 0.')
          .required(translate('general.required')),
      })
      .test(
        'is-greater-than-youngest-single-amount',
        'Cannot be more than 3x the youngest single allowance',
        function (value) {
          const { youngestSingleAmount } = this.parent
          return youngestSingleAmount === null || value <= youngestSingleAmount * 3
        },
      ),

    oldestSingleWithDependentsAmount: Yup.number()
      .nullable()
      .when(['allowanceOptionId'], {
        is: (allowanceOptionId: AllowanceTypes) => allowanceOptionId === AllowanceTypes.FamilyAgeBasedAllowance,
        then: Yup.number()
          .nullable()
          .typeError('Must be a number.')
          .min(Yup.ref('oldestSingleAmount'), 'Must be greater than single allowance.')
          .required(translate('general.required')),
      })
      .test(
        'is-greater-than-youngest-single-amount',
        'Cannot be more than 3x the youngest single with dependents allowance',
        function (value) {
          const { youngestSingleWithDependentsAmount } = this.parent
          return youngestSingleWithDependentsAmount === null || value <= youngestSingleWithDependentsAmount * 3
        },
      ),

    oldestMarriedAmount: Yup.number()
      .nullable()
      .when(['allowanceOptionId'], {
        is: (allowanceOptionId: AllowanceTypes) => allowanceOptionId === AllowanceTypes.FamilyAgeBasedAllowance,
        then: Yup.number()
          .nullable()
          .typeError('Must be a number.')
          .min(Yup.ref('oldestSingleAmount'), 'Must be greater than single allowance.')
          .required(translate('general.required')),
      })
      .test(
        'is-greater-than-youngest-single-amount',
        'Cannot be more than 3x the youngest married allowance',
        function (value) {
          const { youngestMarriedAmount } = this.parent
          return youngestMarriedAmount === null || value <= youngestMarriedAmount * 3
        },
      ),

    oldestMarriedWithDependentsAmount: Yup.number()
      .nullable()
      .when(['allowanceOptionId'], {
        is: (allowanceOptionId: AllowanceTypes) => allowanceOptionId === AllowanceTypes.FamilyAgeBasedAllowance,
        then: Yup.number()
          .nullable()
          .typeError('Must be a number.')
          .min(Yup.ref('oldestSingleAmount'), 'Must be greater than single allowance.')
          .required(translate('general.required')),
      })
      .test(
        'is-greater-than-youngest-single-amount',
        'Cannot be more than 3x the youngest married with dependents allowance',
        function (value) {
          const { youngestMarriedWithDependentsAmount } = this.parent
          return youngestMarriedWithDependentsAmount === null || value <= youngestMarriedWithDependentsAmount * 3
        },
      ),

    youngestFamilyAge: Yup.number()
      .nullable()
      .when('allowanceOptionId', {
        is: (allowanceOptionId: AllowanceTypes) => allowanceOptionId === AllowanceTypes.FamilyAgeBasedAllowance,
        then: Yup.number().nullable().required(translate('general.required')),
      }),

    youngestSingleAmount: Yup.number()
      .nullable()
      .when('allowanceOptionId', {
        is: (allowanceOptionId: AllowanceTypes) => allowanceOptionId === AllowanceTypes.FamilyAgeBasedAllowance,
        then: Yup.number()
          .nullable()
          .typeError('Must be a number.')
          .min(1, 'Must be greater than 0.')
          .max(Yup.ref('oldestSingleAmount'), 'Youngest single amount cannot be greater than oldest single amount')
          .required(translate('general.required')),
      }),

    youngestSingleWithDependentsAmount: Yup.number()
      .nullable()
      .when(['allowanceOptionId'], {
        is: (allowanceOptionId: AllowanceTypes) => allowanceOptionId === AllowanceTypes.FamilyAgeBasedAllowance,
        then: Yup.number()
          .nullable()
          .typeError('Must be a number.')
          .min(Yup.ref('youngestSingleAmount'), 'Must be greater than single allowance.')
          .max(
            Yup.ref('oldestSingleWithDependentsAmount'),
            'Youngest single with dependents amount cannot be greater than oldest single with dependents amount',
          )
          .required(translate('general.required')),
      }),

    youngestMarriedAmount: Yup.number()
      .nullable()
      .when(['allowanceOptionId'], {
        is: (allowanceOptionId: AllowanceTypes) => allowanceOptionId === AllowanceTypes.FamilyAgeBasedAllowance,
        then: Yup.number()
          .nullable()
          .typeError('Must be a number.')
          .min(Yup.ref('youngestSingleAmount'), 'Must be greater than single allowance.')
          .max(Yup.ref('oldestMarriedAmount'), 'Youngest married amount cannot be greater than oldest married amount')
          .required(translate('general.required')),
      }),

    youngestMarriedWithDependentsAmount: Yup.number()
      .nullable()
      .when(['allowanceOptionId'], {
        is: (allowanceOptionId: AllowanceTypes) => allowanceOptionId === AllowanceTypes.FamilyAgeBasedAllowance,
        then: Yup.number()
          .nullable()
          .typeError('Must be a number.')
          .min(Yup.ref('youngestSingleAmount'), 'Must be greater than single allowance.')
          .max(
            Yup.ref('oldestMarriedWithDependentsAmount'),
            'Youngest married with dependents amount cannot be greater than oldest married with dependents amount',
          )
          .required(translate('general.required')),
      }),
  })

export default SetAllowances
